"use client";

import type React from "react";
import { useEffect, useState, useMemo } from "react";
import styles from "./ViewSegmentAdditionModal.module.css";
import { FiAlertTriangle, FiX } from "react-icons/fi";
import { useGetAllSegments } from "API/Queries/useGetAllSegments";
import { useParams } from "react-router-dom";
import type { Segment } from "Interfaces/FeatureFlip";
import { useAddMappedSegments } from "API/Queries/useAddMappedSegments";
import { useGetMappedSegments } from "API/Queries/useGetMappedSegmentValue";
import { useDeleteMappedSegment } from "API/Queries/useDeleteMappedSegments";

interface IViewSegmentAdditionModalProps {
  selectedTreatmentName: string;
  selectedTreatmentId: string;
  onClose: () => void;
  onSave?: (segments: Segment[]) => void;
}

interface SegmentItem {
  id: string;
  name: string;
}

const ViewSegmentAdditionModal = (props: IViewSegmentAdditionModalProps) => {
  const { selectedTreatmentName, selectedTreatmentId, onClose, onSave } = props;

  const { id, featureId } = useParams();
  const [searchTerm, setSearchTerm] = useState("");
  const [savedValues, setSavedValues] = useState<SegmentItem[]>([]);
  const [removedValues, setRemovedValues] = useState<SegmentItem[]>([]);
  const [newValues, setNewValues] = useState<SegmentItem[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

  const {
    data: segments,
    refetch: getAllSegments,
    isLoading,
  } = useGetAllSegments({
    query: `projectId==${id}${searchTerm ? `;name==%${searchTerm}%` : ""}`,
  });

  const { mutateAsync: getAllMappedSegments } = useGetMappedSegments();
  const { mutateAsync: addMappedSegments } = useAddMappedSegments();
  const { mutateAsync: deleteMappedSegments } = useDeleteMappedSegment();

  // Extract complex logic
  const hasChanges = removedValues.length > 0 || newValues.length > 0;
  const hasSegments = segments && segments.length > 0;
  const hasNoResults = !isLoading && segments && segments.length === 0;
  const shouldShowSegmentList = searchTerm && hasSegments;
  const shouldShowNoResults = searchTerm && hasNoResults;
  const shouldShowConfirmModal = hasChanges;

  useEffect(() => {
    const debouncedSearch = setTimeout(() => {
      if (searchTerm) {
        getAllSegments();
      }
    }, 300);
    return () => clearTimeout(debouncedSearch);
  }, [searchTerm, getAllSegments]);

  useEffect(() => {
    getAllMappedSegments(selectedTreatmentId, {
      onSuccess: (data) => {
        const mappedSegments = data.map((segment) => ({
          id: segment.id,
          name: segment.name,
        }));
        setSavedValues(mappedSegments);
      },
      onError: (error) => {
        console.error("Failed to fetch mapped segments:", error);
      },
    });
  }, [selectedTreatmentId, getAllMappedSegments]);

  const selectedSegments = useMemo(() => {
    return [
      ...savedValues.filter((s) => !removedValues.some((r) => r.id === s.id)),
      ...newValues,
    ];
  }, [savedValues, newValues, removedValues]);

  // Extract event handlers
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSegmentSelect = (segment: SegmentItem) => {
    const isSaved = savedValues.some((s) => s.id === segment.id);
    const isRemoved = removedValues.some((r) => r.id === segment.id);
    const isNew = newValues.some((s) => s.id === segment.id);

    if (isRemoved) {
      setRemovedValues(removedValues.filter((r) => r.id !== segment.id));
    } else if (!isSaved && !isNew) {
      setNewValues([...newValues, { id: segment.id, name: segment.name }]);
    }
    setSearchTerm("");
  };

  const handleRemoveSegment = (segmentId: string) => {
    const savedSegment = savedValues.find((s) => s.id === segmentId);
    if (savedSegment) {
      setRemovedValues([...removedValues, savedSegment]);
    }

    const isNewSegment = newValues.some((s) => s.id === segmentId);
    if (isNewSegment) {
      setNewValues(newValues.filter((s) => s.id !== segmentId));
    }
  };

  const handleSegmentItemClick = (segment: SegmentItem) => () => {
    handleSegmentSelect(segment);
  };

  const handleRemoveSegmentClick = (segmentId: string) => () => {
    handleRemoveSegment(segmentId);
  };

  const handleCloseModal = () => {
    if (shouldShowConfirmModal) {
      setShowConfirmModal(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmModal(false);
  };

  const handleDiscardChanges = () => {
    onClose();
  };

  const handleSave = async () => {
    try {
      const createPromises =
        newValues.length > 0
          ? newValues.map((segment) => {
              const payload = {
                treatmentId: selectedTreatmentId,
                segmentId: segment.id,
                projectId: String(id),
                featureId: String(featureId),
              };
              return addMappedSegments(payload);
            })
          : [];

      const deletePromises =
        removedValues.length > 0
          ? removedValues.map((segment) => {
              const payload = {
                treatmentId: selectedTreatmentId,
                segmentId: segment.id,
                projectId: String(id),
                featureId: String(featureId),
              };
              return deleteMappedSegments(payload);
            })
          : [];

      await Promise.all([...createPromises, ...deletePromises]);
      onClose();
    } catch (err) {
      console.error("Error while saving mapped segments:", err);
    }
  };

  // Render confirmation modal
  if (showConfirmModal) {
    return (
      <div className={styles.confirmModal}>
        <div className={styles.confirmContent}>
          <div className={styles.confirmHeader}>
            <FiAlertTriangle className={styles.warningIcon} />
            <h2>Unsaved Changes</h2>
          </div>
          <p className={styles.confirmMessage}>
            You have unsaved changes in your segment. Are you sure you want to
            close without saving?
          </p>
          <div className={styles.confirmActions}>
            <button
              className={styles.cancelButton}
              onClick={handleConfirmClose}
            >
              Keep Editing
            </button>
            <button
              className={styles.discardButton}
              onClick={handleDiscardChanges}
            >
              Discard Changes
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.modal}>
      <div className={styles.modalContent}>
        <div className={styles.modalHeader}>
          <div className={styles.headerContent}>
            <h2>Manage Segments - {selectedTreatmentName}</h2>
          </div>
          <button className={styles.closeButton} onClick={handleCloseModal}>
            <FiX />
          </button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.segmentContainer}>
            <div className={styles.segmentInput}>
              <input
                type="text"
                placeholder="Search segments..."
                value={searchTerm}
                onChange={handleSearchChange}
                className={styles.searchInput}
              />

              {shouldShowSegmentList && (
                <div className={styles.segmentList}>
                  {segments.map((segment) => (
                    <div
                      key={segment.id}
                      className={styles.segmentItem}
                      onClick={handleSegmentItemClick(segment)}
                    >
                      {segment.name}
                    </div>
                  ))}
                </div>
              )}

              {searchTerm && isLoading && (
                <div className={styles.loadingIndicator}>Loading...</div>
              )}

              {shouldShowNoResults && (
                <div className={styles.noResults}>No segments found</div>
              )}
            </div>

            <div className={styles.selectedSegments}>
              {selectedSegments.map((segment: SegmentItem) => (
                <div key={segment.id} className={styles.segmentTag}>
                  {segment.name}
                  <button
                    onClick={handleRemoveSegmentClick(segment.id)}
                    className={styles.removeButton}
                  >
                    <FiX />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          <button
            className={styles.saveButton}
            onClick={handleSave}
            disabled={!hasChanges}
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewSegmentAdditionModal;
